import type { ReactNode } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { CapacitySlots, type Capacity } from '@/features/concurrency';
import { STATUS_PRESENTATION, TASK_STATE_WORDS } from '@/features/tasks';
import { QUIET_FIGURE } from '@/features/workspace-overview/stateStyles';
import { formatSpan } from '@/shared/utils/time';

import {
  clearFilters,
  isTileView,
  tileView,
  type TaskListView,
  type TileKey,
} from '../listView';
import type { TaskTiles as TaskTilesFigures } from '../taskTiles';

export interface TaskTilesProps {
  tiles: TaskTilesFigures;
  /**
   * The workspace's limit and load (spec 118). Present, the first tile is Runs at
   * once; absent (an older service), it is In progress as before.
   */
  capacity?: Capacity | null;
  view: TaskListView;
  onViewChange: (next: TaskListView) => void;
}

type Tone = 'running' | 'waiting' | 'failed' | 'done';

const DOT: Record<Tone, string> = {
  running: 'bg-board-state-running',
  waiting: 'bg-board-state-waiting',
  failed: 'bg-board-state-failed',
  done: 'bg-board-state-done',
};

const FIGURE: Record<Tone, string> = {
  running: 'text-board-state-running',
  waiting: 'text-board-state-waiting',
  failed: 'text-board-state-failed',
  done: 'text-board-state-done',
};

/**
 * One cell of the strip (spec 127 FR-009). The tiles were five 130px cards
 * above the table; they are one line now, still each a button applying its
 * filter, so the first task row starts where the eye expects the page to.
 */
const TILE =
  'flex min-w-0 flex-auto items-center gap-2 px-3.5 py-2 text-left md:first:rounded-s-lg md:last:rounded-e-lg max-md:border-border max-md:not-last:border-b max-md:odd:not-last:border-e max-md:last:col-span-2';
/**
 * On a phone, two even columns with Completed across the foot: wrapped, the
 * strip broke into lines of one, two and one tiles of uneven widths (spec 271).
 */
const STRIP =
  'border-border bg-card divide-border flex flex-wrap divide-x rounded-lg border max-md:grid max-md:grid-cols-2 max-md:divide-x-0 max-md:overflow-hidden';

/**
 * How the workspace is doing, before anyone reads a row (spec 110 US1).
 *
 * Counted over the whole workspace, whatever the list below is narrowed to —
 * see `summarizeTaskTiles`. Words are the board's own: "In progress" and
 * "Errors" are `TASK_STATE_WORDS`, "Spend limit" is the blocked state's card
 * label, "Completed" the done column's canonical name. A tile that named a state
 * differently from the board would be a second vocabulary for the same task.
 *
 * **Every tile is a way in** (spec 112 US4). Pressing one applies the view
 * that lists exactly what it counted — replacing the filters and the search,
 * because merging would list fewer — and pressing it again clears them. It
 * shows as pressed exactly while the filters in effect are its own.
 *
 * Colour only above zero, as on Overview's counters: on a quiet workspace the
 * row reads grey, and colour anywhere in it means something is happening.
 *
 * **All five, always, in one order** (spec 113 FR-005, superseding spec 110
 * FR-006). Errors, Needs a person and Spend limit used to disappear at zero,
 * and the page refreshes while work is live — so the first failure inserted a
 * tile and moved every tile after it, including the one about to be pressed.
 * A grey "0 Errors" is also worth reading on a page whose job is monitoring.
 */
export function TaskTiles({
  tiles,
  capacity,
  view,
  onViewChange,
}: TaskTilesProps) {
  const press = (tile: TileKey) => {
    onViewChange(
      isTileView(view, tile)
        ? { ...clearFilters(view), hidden: view.hidden }
        : tileView(tile, view),
    );
  };
  const tile = (key: TileKey) => ({
    testId: key,
    pressed: isTileView(view, key),
    onPress: () => {
      press(key);
    },
  });

  return (
    <div
      role="group"
      aria-label="Task summary"
      data-testid="task-tiles"
      className={STRIP}
    >
      {capacity ? (
        // How busy the workspace is (spec 118 FR-010, superseding 116 FR-002):
        // running and waiting are one question, so the number counts both and the
        // press lists both. The slots and the figures are the service's; the number
        // is the rows the press lists, so the two always agree.
        <Tile
          {...tile('runs-at-once')}
          tone="running"
          value={tiles.runsAtOnce}
          label="Runs at once"
          slots={
            <CapacitySlots
              running={capacity.running}
              limit={capacity.limit}
              queued={capacity.queued}
              decorative
            />
          }
          note={`${String(capacity.running)} of ${String(capacity.limit)}${
            capacity.queued > 0 ? ` · ${String(capacity.queued)} waiting` : ''
          }`}
        />
      ) : (
        <Tile
          {...tile('running')}
          tone="running"
          value={tiles.running}
          label={TASK_STATE_WORDS.running.many}
          // What waits behind the running work (spec 116 FR-002) — said, never
          // added: without capacity figures the number and press stay running only.
          note={tiles.queued > 0 ? `${String(tiles.queued)} queued` : undefined}
        />
      )}
      <Tile
        {...tile('needs-person')}
        tone="waiting"
        value={tiles.needsPerson}
        label="Needs a person"
        hint="at a gate, or flagged"
      />
      <Tile
        {...tile('failed')}
        tone="failed"
        value={tiles.failed}
        label={TASK_STATE_WORDS.failed.many}
      />
      <Tile
        {...tile('blocked')}
        tone="waiting"
        value={tiles.blocked}
        label={STATUS_PRESENTATION.blocked?.cardLabel ?? 'Spend limit'}
        hint="an administrator can raise it"
      />
      <Tile
        {...tile('completed')}
        tone="done"
        value={tiles.completedLast7d}
        label="Completed · last 7 days"
        // Data, not a hint: on the strip it stays visible.
        note={
          tiles.medianLeadMs === null
            ? undefined
            : `median ${formatSpan(tiles.medianLeadMs)}`
        }
      />
    </div>
  );
}

function Figure({ tone, value }: { tone: Tone; value: number }) {
  return (
    <span
      className={`text-xl font-light tabular-nums ${value > 0 ? FIGURE[tone] : QUIET_FIGURE}`}
    >
      {value}
    </span>
  );
}

function Label({
  tone,
  value,
  label,
}: {
  tone: Tone;
  value: number;
  label: string;
}) {
  return (
    <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
      <span
        aria-hidden="true"
        className={`size-2 shrink-0 rounded-full ${value > 0 ? DOT[tone] : 'bg-muted-foreground/30'}`}
      />
      <span className="truncate">{label}</span>
    </span>
  );
}

/** A tile: the figure, the word, a hint — and a button applying its filter. */
function Tile({
  testId,
  tone,
  value,
  label,
  hint,
  note,
  slots,
  pressed,
  onPress,
}: {
  testId: string;
  tone: Tone;
  value: number;
  label: string;
  hint?: string;
  /**
   * A fact beside the figure that holds whatever the figure is — unlike `hint`,
   * which goes quiet at zero. "3 queued" under In progress matters most exactly
   * when nothing is running yet (spec 116 FR-002).
   */
  note?: string;
  /** A drawing of the figure, between the word and the note (spec 118: run slots). */
  slots?: ReactNode;
  pressed: boolean;
  onPress: () => void;
}) {
  return (
    <button
      type="button"
      data-testid={`task-tile-${testId}`}
      aria-pressed={pressed}
      onClick={onPress}
      // A hint about gates or administrators is noise when there is nothing
      // there (spec 113); on the strip it is the cell's tooltip.
      title={hint && value > 0 ? hint : undefined}
      className={`${TILE} hover:bg-accent/50 focus-visible:ring-ring cursor-pointer transition-colors focus-visible:ring-3 focus-visible:outline-none ${
        pressed ? 'bg-selected' : ''
      }`}
    >
      <Figure tone={tone} value={value} />
      <Label tone={tone} value={value} label={label} />
      {/* The header's meter says the same; a half-width tile has no room. */}
      {slots ? <span className="contents max-md:hidden">{slots}</span> : null}
      {note ? (
        <span className="text-muted-foreground truncate text-xs tabular-nums">
          {note}
        </span>
      ) : null}
    </button>
  );
}

/** The tiles' footprint while the workspace loads, so nothing below jumps (spec 079). */
export function TaskTilesSkeleton() {
  return (
    <div aria-hidden="true" className={STRIP}>
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className={TILE}>
          <Skeleton className="h-5 w-6 rounded" />
          <Skeleton className="h-3 w-20 rounded" />
        </div>
      ))}
    </div>
  );
}
